import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import {
  HomeIcon,
  BookOpenIcon, DocumentArrowUpIcon,
  FolderOpenIcon,
  InboxIcon,
  SparklesIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ListBulletIcon,
  MegaphoneIcon,
  IdentificationIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ClockIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  TrophyIcon,
  BanknotesIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const navSections = [
  {
    label: 'Overview',
    roles: ['director', 'admin', 'teacher', 'float', 'cook'],
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: HomeIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/calendar', label: 'Calendar', icon: CalendarDaysIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      // HIDDEN UNTIL A POLICY IS PUBLISHED. Ruled 2026-09-19: do not show a
      // thing that cannot be acted on. Appears the moment a director
      // publishes v1 — the flag rides on the counts poll already running.
      { to: '/my/policies', label: 'Policies', icon: BookOpenIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'],
        showIf: (c) => !!c.policies_published },
      // ALWAYS VISIBLE TO A DIRECTOR, published or not — this is the screen
      // that does the publishing, so gating it on a publication existing
      // would be the deadlock this page was added to fix.
      { to: '/admin/policies', label: 'Publish Policies', icon: DocumentArrowUpIcon, roles: ['director'] },
      { to: '/my/file', label: 'My Employment File', icon: FolderOpenIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/my/assessment', label: 'Talent Assessment', icon: SparklesIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/my/scorecard', label: 'My Scorecard', icon: TrophyIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
    ],
  },
  {
    label: 'People',
    roles: ['director', 'admin', 'teacher', 'float', 'cook'],
    items: [
      { to: '/recruitment', label: 'Recruitment', icon: UserPlusIcon, roles: ['director', 'admin'], badgeKey: 'new_applications' },
      { to: '/interview-slots', label: 'Interview Slots', icon: CalendarDaysIcon, roles: ['director', 'admin'] },
      { to: '/onboarding', label: 'Onboarding', icon: ClipboardDocumentCheckIcon, roles: ['director', 'admin'] },
      { to: '/pending-approvals', label: 'Pending Approvals', icon: InboxIcon, roles: ['director', 'admin'], badgeKey: 'pending_approvals' },
      { to: '/wallet-approvals', label: 'Cashout Approvals', icon: BanknotesIcon, roles: ['director', 'admin'] },
      { to: '/doctor-note-approvals', label: 'Doctor Note Approvals', icon: DocumentTextIcon, roles: ['director', 'admin'] },
      { to: '/my/doctor-notes', label: 'My Doctor Notes', icon: DocumentTextIcon, roles: ['teacher', 'float', 'cook'] },
      { to: '/file-health', label: 'File Health', icon: ChartBarIcon, roles: ['director', 'admin'] },
      { to: '/staff', label: 'Staff Directory', icon: UserGroupIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
    ],
  },
  {
    label: 'Compliance',
    roles: ['director', 'admin', 'teacher', 'float', 'cook'],
    items: [
      { to: '/compliance', label: 'Compliance', icon: ShieldCheckIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
    ],
  },
  {
    label: 'Accountability',
    roles: ['director', 'admin', 'teacher', 'float', 'cook'],
    items: [
      { to: '/clock', label: 'Time Clock (me)', icon: ClockIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/my/time', label: 'My Time History', icon: ClockIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/my/schedule', label: 'My Schedule', icon: CalendarDaysIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      // UNMATCHED PUNCHES. A clock-in with no clock-out past the 16h ceiling
      // is a shift credited ZERO. Six sat unseen for up to four weeks and were
      // found only because payroll looked wrong. Director-only: it is a wage
      // figure and the correction is a director in the admin UI.
      { to: '/time-clock', label: 'Time Clock (admin)', icon: ClockIcon, roles: ['director', 'admin'],
        badgeKey: 'unmatched_punches' },
      { to: '/attendance', label: 'Attendance', icon: ClockIcon, roles: ['director', 'admin'] },
      { to: '/clocked-hours', label: 'Clocked Hours', icon: ClockIcon, roles: ['director'] },
      { to: '/team-schedule', label: 'Team Schedule', icon: CalendarDaysIcon, roles: ['director', 'admin'] },
      { to: '/time-off', label: 'Time Off', icon: CalendarDaysIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'], badgeKey: 'pending_time_off' },
      { to: '/availability-requests', label: 'Availability Requests', icon: CalendarDaysIcon, roles: ['director'], badgeKey: 'pending_availability_requests' },
      { to: '/enrollment-forecast', label: 'Enrollment Forecast', icon: UserGroupIcon, roles: ['director', 'admin'] },
      { to: '/classrooms', label: 'Classrooms', icon: AcademicCapIcon, roles: ['director'] },
      { to: '/observations', label: 'Observations', icon: EyeIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/one-on-ones', label: '1:1 Meetings', icon: ChatBubbleLeftRightIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/disciplinary', label: 'Disciplinary', icon: ExclamationTriangleIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/performance', label: 'Performance', icon: ChartBarIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'] },
      { to: '/scorecards', label: 'Goal Scorecards', icon: TrophyIcon, roles: ['director'] },
      { to: '/goals/templates', label: 'Goal Templates', icon: TrophyIcon, roles: ['director'] },
      { to: '/goals/comp-tiers', label: 'Compensation Tiers', icon: TrophyIcon, roles: ['director'] },
      { to: '/holidays', label: 'Holidays & Closures', icon: CalendarDaysIcon, roles: ['director'] },
      { to: '/pay-rules', label: 'Pay Rules', icon: CalendarDaysIcon, roles: ['director'] },
    ],
  },
  {
    label: 'Operations',
    roles: ['director', 'admin', 'teacher', 'float', 'cook'],
    items: [
      { to: '/command-center', label: 'Command Center', icon: PresentationChartLineIcon, roles: ['director'] },
      { to: '/ratio-compliance', label: 'Ratio Compliance', icon: UserGroupIcon, roles: ['director', 'admin'] },
      { to: '/tasks', label: 'Tasks', icon: ListBulletIcon, roles: ['director', 'admin', 'teacher', 'float', 'cook'], badgeKey: 'my_open_tasks' },
      { to: '/messages', label: 'Messages', icon: MegaphoneIcon, roles: ['director'] },
      { to: '/contacts', label: 'Contacts', icon: IdentificationIcon, roles: ['director', 'admin'] },
      { to: '/account-vault', label: 'Account Vault', icon: LockClosedIcon, roles: ['director', 'admin'] },
      { to: '/payroll', label: 'Payroll', icon: ChartBarIcon, roles: ['director'] },
      { to: '/reports', label: 'Reports & KPIs', icon: ChartBarIcon, roles: ['director'] },
      { to: '/activity', label: 'Activity Log', icon: ListBulletIcon, roles: ['director'] },
      { to: '/search', label: 'Search', icon: MagnifyingGlassIcon, roles: ['director'] },
      { to: '/settings', label: 'Settings', icon: Cog6ToothIcon, roles: ['director'] },
    ],
  },
];

export default function Sidebar({ drawerOpen = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({});

  // Refresh sidebar badges every 60s. Cheap query, scoped per-user.
  const loadCounts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get('/notifications/counts');
      setCounts(data);
    } catch { /* silent — sidebar badges aren't critical */ }
  }, [user?.id]);

  useEffect(() => {
    loadCounts();
    const id = setInterval(loadCounts, 60 * 1000);
    return () => clearInterval(id);
  }, [loadCounts]);

  // Refresh on route change so a count drops as soon as you act on something.
  useEffect(() => { loadCounts(); }, [location.pathname, loadCounts]);
  const [expandedSections, setExpandedSections] = useState(() => {
    // Auto-expand section containing current route
    const expanded = {};
    navSections.forEach((section) => {
      if (section.items.some((item) => location.pathname.startsWith(item.to))) {
        expanded[section.label] = true;
      }
    });
    // Always expand Overview
    expanded['Overview'] = true;
    return expanded;
  });

  const toggleSection = (label) => {
    setExpandedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const roleLabel = {
    director: 'Director',
    lead_admin: 'Lead Admin',
    staff: 'Staff',
    cook: 'Cook',
  };

  return (
    <aside className={`fixed inset-y-0 left-0 w-60 bg-gray-900 text-white flex flex-col z-50 md:z-30 transform transition-transform duration-200 ${drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
      {/* Header */}
      <div className="px-5 py-5 border-b border-gray-800">
        <h1 className="text-lg font-bold tracking-tight">BBC People Portal</h1>
        <p className="text-xs text-gray-500 mt-0.5">Bright Beginnings Childcare</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-3">
        {navSections
          .filter((section) => section.roles.includes(user?.role))
          .map((section) => (
            <div key={section.label} className="mb-1">
              <button
                onClick={() => toggleSection(section.label)}
                className="w-full flex items-center justify-between px-2 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
              >
                {section.label}
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 transition-transform ${
                    expandedSections[section.label] ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedSections[section.label] && (
                <div className="space-y-0.5">
                  {section.items
                    .filter((item) => item.roles.includes(user?.role))
                    .filter((item) => !item.showIf || item.showIf(counts))
                    .map((item) => {
                      const count = item.badgeKey ? counts[item.badgeKey] || 0 : 0;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? 'bg-gray-800 text-white border-r-4 border-orange-500'
                                : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                            }`
                          }
                        >
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          <span className="flex-1">{item.label}</span>
                          {count > 0 && (
                            <span className="text-[11px] font-semibold bg-orange-500 text-white rounded-full px-2 py-0.5 min-w-[20px] text-center">
                              {count > 99 ? '99+' : count}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                </div>
              )}
            </div>
          ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-gray-800">
        <NavLink
          to={user?.id ? `/staff/${user.id}` : '#'}
          title="View my profile"
          className="flex items-center gap-3 mb-3 hover:bg-gray-800/60 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-sm font-bold">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-gray-500">{roleLabel[user?.role] || user?.role} · view profile</p>
          </div>
        </NavLink>
        <button
          onClick={logout}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors w-full"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
